import { fetch_company_cases } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_live_queue';
import {
  normalize_key,
  normalize_text,
  read_session_project_id,
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';

function resolved_project_id(project_id = null) {
  return project_id || read_session_project_id();
}

function status_key(value = '') {
  return normalize_key(value);
}

function lower_text(value = '') {
  return normalize_text(value).toLowerCase();
}

function display_text(value = '') {
  return String(value || '')
    .replace(/Ã‚Â·/g, '·')
    .replace(/Â·/g, '·')
    .trim();
}

function split_detail_tokens(value = '') {
  return display_text(value)
    .split('·')
    .map((token) => token.trim())
    .filter(Boolean);
}

function is_warning_case(case_item = {}) {
  const value = lower_text([
    case_item?.sla,
    case_item?.sla_runtime?.status,
    case_item?.sla_runtime?.status_label,
    case_item?.sla_runtime?.badge_label,
    case_item?.due_status,
  ].filter(Boolean).join(' '));

  return value.includes('left')
    || value.includes('warning')
    || value.includes('at-risk')
    || value.includes('due');
}

function is_breached_case(case_item = {}) {
  const value = lower_text([
    case_item?.sla,
    case_item?.sla_runtime?.status,
    case_item?.sla_runtime?.status_label,
    case_item?.sla_runtime?.badge_label,
    case_item?.due_status,
  ].filter(Boolean).join(' '));

  return value.includes('overdue')
    || value.includes('breach')
    || value.includes('breached')
    || value.includes('escalated');
}

function formatted_risk_label(label = '', score = '') {
  const normalized_label = lower_text(label);
  const numeric_score = Number(score);

  if (normalized_label.includes('high') || numeric_score >= 70) return 'High';
  if (normalized_label.includes('mid') || normalized_label.includes('medium') || numeric_score >= 40) return 'Medium';
  if (normalized_label.includes('low') || numeric_score > 0) return 'Low';
  return 'New';
}

function formatted_risk_class(label = '', score = '') {
  const normalized_label = formatted_risk_label(label, score).toLowerCase();
  if (normalized_label === 'high') return 'is-high';
  if (normalized_label === 'medium') return 'is-medium';
  if (normalized_label === 'low') return 'is-low';
  return 'is-muted';
}

function formatted_risk(case_item = {}) {
  const score = normalize_text(case_item?.risk_score || '0');
  const label = formatted_risk_label(case_item?.risk_label, case_item?.risk_score);
  return {
    risk: `${score} · ${label}`,
    risk_class: formatted_risk_class(label, score),
  };
}

function simplified_sla_class(case_item = {}) {
  if (is_breached_case(case_item)) return 'is-danger';
  if (is_warning_case(case_item)) return 'is-warning';
  return 'is-success';
}

function is_unassigned_owner(value = '') {
  const normalized = status_key(value);
  return !normalized || normalized === 'unassigned' || normalized === 'none' || normalized === 'na';
}

function owner_label(case_item = {}) {
  const owner = normalize_text(case_item?.owner);
  return is_unassigned_owner(owner) ? '' : owner;
}

function domain_class(case_item = {}) {
  const key = status_key(case_item?.domain_key || case_item?.domain || 'case');
  return key ? `is-${key}` : 'is-case';
}

function live_case_detail(case_item = {}) {
  const detail = normalize_text(case_item?.subtitle);
  if (detail) return detail;
  return owner_label(case_item);
}

function inferred_contact(requester = '') {
  const normalized = lower_text(requester);
  const contact_lookup = {
    't. dlamini': '082 551 0098',
    'm. botha': '082 410 1107',
    'anon (call)': 'Call centre escalation',
    'r. naicker': '083 448 2219',
    'p. khoza': '073 884 1902',
    's. mthembu': '071 350 4410',
    'g. adams': '081 440 7782',
  };

  return contact_lookup[normalized] || 'Contact to be confirmed';
}

function entity_type_match_key(value = '') {
  return status_key(display_text(value).replace(/\s*[-·]\s*/g, ' '));
}

function type_class_from_case_type(case_type = '') {
  const normalized = status_key(case_type);
  if (normalized.includes('claims')) return 'is-claims';
  if (normalized.includes('legal')) return 'is-legal';
  if (normalized.includes('service')) return 'is-service';
  if (normalized.includes('oversight')) return 'is-oversight';
  if (normalized.includes('hr')) return 'is-hr';
  if (normalized.includes('safety')) return 'is-safety';
  return 'is-case';
}

function is_triage_case(case_item = {}) {
  const status = status_key(case_item?.status);
  return status.includes('triage')
    || status.includes('new')
    || status.includes('pending-verification')
    || status.includes('registered');
}

function is_routed_case(case_item = {}) {
  return Boolean(owner_label(case_item));
}

function is_operations_case(case_item = {}) {
  const status = status_key(case_item?.status);
  return status.includes('ops') || status.includes('operation');
}

export async function fetch_intake_cases(project_id = null) {
  const cases = await fetch_company_cases(resolved_project_id(project_id));
  return cases.map((case_item) => {
    const risk = formatted_risk(case_item);
    return {
      ...case_item,
      detail: live_case_detail(case_item),
      type: normalize_text(case_item?.domain) || 'Case',
      type_class: domain_class(case_item),
      stage: normalize_text(case_item?.status) || 'New case',
      routed_to: owner_label(case_item),
      domain_class: domain_class(case_item),
      owner: owner_label(case_item),
      sla_class: simplified_sla_class(case_item),
      ...risk,
    };
  });
}

export function map_cases_to_inbound_requests(cases = []) {
  return cases.map((case_item, index) => {
    const detail = display_text(case_item?.subtitle) || owner_label(case_item);
    const detail_tokens = split_detail_tokens(detail);
    const requester = display_text(
      case_item?.user_name
      || case_item?.user_full_name
      || case_item?.raw_record?.user_name
      || case_item?.raw_record?.user_full_name
      || case_item?.requester_name
      || case_item?.raw_record?.requester_name
      || detail_tokens[0]
      || case_item?.raw_record?.requester
      || case_item?.raw_record?.requestor
      || case_item?.raw_record?.claimant
      || case_item?.raw_record?.created_by_name
      || case_item?.raw_record?.created_by?.full_name
      || case_item?.raw_record?.created_by?.name
      || [
        case_item?.raw_record?.created_by?.first_name,
        case_item?.raw_record?.created_by?.last_name,
      ].filter(Boolean).join(' ')
      || case_item?.raw_record?.user?.full_name
      || case_item?.raw_record?.user?.name
      || [
        case_item?.raw_record?.user?.first_name,
        case_item?.raw_record?.user?.last_name,
      ].filter(Boolean).join(' ')
      || case_item?.raw_record?.opened_by_name
      || case_item?.raw_record?.submitted_by_name
    );

    return {
      id: case_item?.case_code || `inbound-case-${index + 1}`,
      title: display_text(case_item?.title) || normalize_text(case_item?.case_code) || 'Case',
      detail,
      requester,
      location: display_text(
        case_item?.raw_record?.location
        || case_item?.raw_record?.address
        || case_item?.raw_record?.physical_address
        || detail_tokens[1]
      ),
      contact: display_text(
        case_item?.user_email
        || case_item?.raw_record?.user_email
        || case_item?.raw_record?.contact
        || case_item?.raw_record?.phone
        || case_item?.raw_record?.email
        || case_item?.raw_record?.requester_email
        || case_item?.requester_email
      ),
    channel: 'portal',
    channel_label: 'Portal',
    channel_class: 'is-portal',
    suggested_type: display_text(case_item?.domain) || 'Case',
    flag: display_text(case_item?.status) || '',
    flag_class: simplified_sla_class(case_item),
    case_code: case_item?.case_code,
    application_id: case_item?.application_id,
    entity_profile_id: case_item?.entity_profile_id,
    entity_type_id: case_item?.entity_type_id,
    };
  });
}

export function build_register_form(request_item = {}) {
  const detail_tokens = split_detail_tokens(request_item?.detail);
  const requester = display_text(
    request_item?.requester
    || detail_tokens[0]
    || ''
  );
  const location = display_text(
    request_item?.location
    || detail_tokens[1]
    || ''
  );
  const age = display_text(
    request_item?.age
    || detail_tokens[detail_tokens.length - 1]
    || 'Just now'
  );

  return {
    title: `Register · ${display_text(request_item?.id || request_item?.case_code || 'Inbound request')}`,
    subtitle: `${display_text(request_item?.channel_label || 'Portal')} · ${age}`,
    description: display_text(request_item?.description || request_item?.title || ''),
    requester,
    contact: display_text(request_item?.contact || inferred_contact(requester)),
    location,
    case_type: '',
  };
}

export function resolve_entity_type_option(request_item = {}, entity_type_options = []) {
  const request_type = entity_type_match_key(request_item?.suggested_type || request_item?.domain || '');
  if (!request_type) return entity_type_options[0] || null;

  return entity_type_options.find((option) =>
    entity_type_match_key(option?.label) === request_type
  ) || entity_type_options[0] || null;
}

export function build_main_db_case(request_item = {}, register_form = {}, case_type_label = '') {
  const case_type = display_text(case_type_label || request_item?.suggested_type || 'Case');
  return {
    case_code: request_item?.case_code || display_text(request_item?.id || 'Case'),
    title: display_text(request_item?.title) || 'Case',
    detail: [
      display_text(register_form?.requester),
      display_text(register_form?.location),
      `via ${display_text(request_item?.channel_label || 'Portal')}`,
    ].filter(Boolean).join(' · '),
    type: case_type,
    type_class: type_class_from_case_type(case_type),
    stage: 'Triage',
    routed_to: '',
    application_id: request_item?.application_id,
    entity_profile_id: request_item?.entity_profile_id,
    entity_type_id: request_item?.entity_type_id,
  };
}

export function build_registered_case_filters(cases = []) {
  const dynamic_filters = Array.from(new Set(
    cases
      .map((case_item) => JSON.stringify({
        key: status_key(case_item?.domain_key || case_item?.domain),
        label: normalize_text(case_item?.domain),
      }))
      .filter((item) => item !== '{"key":"","label":""}')
  ))
    .map((item) => JSON.parse(item))
    .sort((left, right) => left.label.localeCompare(right.label));

  return [
    { key: 'all', label: 'All domains' },
    ...dynamic_filters,
    { key: 'high-risk', label: 'High Risk' },
    { key: 'sla-breached', label: 'SLA Breached' },
  ];
}

export function build_registered_case_metrics(cases = []) {
  const open_cases = cases.filter((case_item) => !status_key(case_item?.status).includes('closed'));
  const warning_cases = cases.filter((case_item) => is_warning_case(case_item) && !is_breached_case(case_item));
  const breached_cases = cases.filter(is_breached_case);

  return [
    { label: 'OPEN CASES', value: String(open_cases.length), meta: 'live from main DB', meta_class: '' },
    { label: 'SLA WARNING', value: String(warning_cases.length), meta: '<= 48hr to breach', meta_class: '' },
    { label: 'SLA BREACHED', value: String(breached_cases.length), meta: breached_cases.length ? 'Action required' : 'None', meta_class: '' },
    { label: 'AVG. RESOLUTION', value: '-', meta: `${cases.length} total cases`, meta_class: '' },
  ];
}

export function build_intake_pipeline_steps(cases = [], inbound_cases = cases) {
  const triage_cases = cases.filter(is_triage_case);
  const routed_cases = cases.filter(is_routed_case);
  const operations_cases = cases.filter(is_operations_case);

  return [
    { label: 'Inbound', value: String(inbound_cases.length), meta: 'UNREGISTERED' },
    { label: 'Triage', value: String(triage_cases.length), meta: 'ON MAIN DB' },
    { label: 'Coordinator', value: String(routed_cases.length), meta: 'ROUTED' },
    { label: 'Handler', value: String(routed_cases.length), meta: 'OWNED' },
    { label: 'Operations', value: String(operations_cases.length), meta: 'OPS PEOPLE' },
  ];
}

export function build_intake_summary_metrics(cases = []) {
  const registered_today = cases.filter((case_item) => {
    const created_at = Date.parse(case_item?.raw_record?.created_at || case_item?.raw_record?.date_created || '');
    if (!Number.isFinite(created_at)) return false;

    const created_date = new Date(created_at);
    const today = new Date();
    return created_date.getFullYear() === today.getFullYear()
      && created_date.getMonth() === today.getMonth()
      && created_date.getDate() === today.getDate();
  });
  const triage_cases = cases.filter(is_triage_case);
  const unassigned_cases = cases.filter((case_item) => !owner_label(case_item));

  return [
    { label: 'UNASSIGNED · NEW', value: String(unassigned_cases.length), meta: 'live from shared queue', meta_class: '' },
    { label: 'REGISTERED TODAY', value: String(registered_today.length), meta: 'from main DB', meta_class: 'is-positive' },
    { label: 'AWAITING TRIAGE', value: String(triage_cases.length), meta: 'on the main DB', meta_class: '' },
    { label: 'AVG TIME TO REGISTER', value: '-', meta: 'UI unchanged', meta_class: '' },
  ];
}
